import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import { differenceInSeconds, format } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import pdfToText from "react-pdftotext";
import BankStatementAnalysisPanel from "../../components/BankStatementAnalysisPanel";
import BankStatementGroupReport from "../../components/BankStatementGroupReport";
import BankStatementQuestionAssistant from "../../components/BankStatementQuestionAssistant";

const API_BASE = "https://www.smart-lend.com/api";
const MAX_UPLOAD_FILE_SIZE_MB = 50;
const MAX_UPLOAD_REQUEST_SIZE_MB = 64;
const MAX_UPLOAD_FILE_SIZE_BYTES = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;
const MAX_UPLOAD_REQUEST_SIZE_BYTES = MAX_UPLOAD_REQUEST_SIZE_MB * 1024 * 1024;

const fileSizeLabel = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

const readJsonResponse = async (response) => {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    return { message: text };
  }
};

const responseErrorMessage = (response, json, fallback) => {
  if (response.status === 413) {
    return `This upload is too large. Keep each request below ${MAX_UPLOAD_REQUEST_SIZE_MB}MB, or upload fewer files at a time.`;
  }

  const validationErrors = json?.errors
    ? Object.values(json.errors).flat().filter(Boolean)
    : [];

  return validationErrors[0] || json?.message || fallback;
};

const validatePortalUploadSize = (files, label = "file") => {
  const oversizedFile = files.find((file) => file.size > MAX_UPLOAD_FILE_SIZE_BYTES);
  if (oversizedFile) {
    return `${oversizedFile.name} is ${fileSizeLabel(oversizedFile.size)}. The maximum ${label} size is ${MAX_UPLOAD_FILE_SIZE_MB}MB.`;
  }

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  if (totalSize > MAX_UPLOAD_REQUEST_SIZE_BYTES) {
    return `This upload is ${fileSizeLabel(totalSize)} in total. Please keep each upload request below ${MAX_UPLOAD_REQUEST_SIZE_MB}MB, or upload fewer files at a time.`;
  }

  return "";
};

const REPORT_LOADING_MESSAGES = [
  "Your report is generating.",
  "Reviewing completed statements and cash flow patterns.",
  "Larger statements can take longer to generate.",
  "Preparing the lender view and report sections.",
];

const formatAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }

  return numeric.toLocaleString("en-US", {
    style: "currency",
    currency: "SGD",
  });
};

const formatDate = (value) => {
  if (!value) return "";

  try {
    return format(new Date(value), "MMM dd, yyyy");
  } catch (error) {
    return value;
  }
};

const formatDateTime = (value) => {
  if (!value) return "";

  try {
    return format(new Date(value), "MMM dd, yyyy h:mm:ss a");
  } catch (error) {
    return value;
  }
};

const formatDurationSeconds = (startValue, endValue) => {
  if (!startValue || !endValue) {
    return "-";
  }

  try {
    const seconds = Math.max(
      differenceInSeconds(new Date(endValue), new Date(startValue)),
      0,
    );

    return `${seconds}s`;
  } catch (error) {
    return "-";
  }
};

const normalizeStatementGroupValue = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized === "" ? fallback : normalized;
};

const getStatementMonthTimestamp = (statement) => {
  const rawValue = normalizeStatementGroupValue(
    statement?.statement_month_year,
    "",
  );

  if (!rawValue) return null;

  const normalizedValue = rawValue.replace(/-/g, "/").trim();
  const mmYyyyMatch = normalizedValue.match(/^(\d{1,2})\/(\d{4})$/);
  if (mmYyyyMatch) {
    const month = Number(mmYyyyMatch[1]) - 1;
    const year = Number(mmYyyyMatch[2]);
    if (month >= 0 && month <= 11) {
      return new Date(year, month, 1).getTime();
    }
  }

  const yyyyMmMatch = normalizedValue.match(/^(\d{4})\/(\d{1,2})$/);
  if (yyyyMmMatch) {
    const year = Number(yyyyMmMatch[1]);
    const month = Number(yyyyMmMatch[2]) - 1;
    if (month >= 0 && month <= 11) {
      return new Date(year, month, 1).getTime();
    }
  }

  const parsedDate = new Date(`01 ${rawValue}`);
  if (!Number.isNaN(parsedDate.getTime())) {
    return new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      1,
    ).getTime();
  }

  return null;
};

const compareBankStatementsByMonth = (left, right) => {
  const leftTimestamp = getStatementMonthTimestamp(left);
  const rightTimestamp = getStatementMonthTimestamp(right);

  if (leftTimestamp !== null && rightTimestamp !== null) {
    return leftTimestamp - rightTimestamp;
  }

  if (leftTimestamp !== null) return -1;
  if (rightTimestamp !== null) return 1;

  return normalizeStatementGroupValue(left?.statement_month_year, "").localeCompare(
    normalizeStatementGroupValue(right?.statement_month_year, ""),
  );
};

const buildGroupedAnalysisStatementsFromReport = (report) => {
  const accounts = Array.isArray(report?.accounts) ? report.accounts : [];

  return accounts
    .map((account, accountIndex) => {
      const statements = Array.isArray(account?.monthly_breakdown)
        ? account.monthly_breakdown
        : [];

      return {
        groupKey:
          account?.account_key ||
          `report-account-${account?.account_number || accountIndex}`,
        bank: normalizeStatementGroupValue(account?.bank, "Unknown Bank"),
        accountNumber: normalizeStatementGroupValue(account?.account_number, "-"),
        statements: statements
          .map((statement) => ({
            ...statement,
            bank: account?.bank,
            bank_account_number: account?.account_number,
            statement_month_year:
              statement?.statement_month_year || statement?.month_label,
            currency_summaries:
              statement?.currency_summaries || statement?.currencies || [],
          }))
          .sort(compareBankStatementsByMonth),
      };
    })
    .filter((group) => group.statements.length > 0)
    .sort((left, right) => {
      const bankDiff = left.bank.localeCompare(right.bank);
      if (bankDiff !== 0) return bankDiff;
      return left.accountNumber.localeCompare(right.accountNumber);
    });
};

const normalizeCbsTextKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\b(pte|ltd|limited|singapore|bank|berhad|private)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const cbsLabelPattern = (value) => escapeRegex(value).replace(/\s+/g, "\\s+");

const extractTextBetween = (text, beforeText, afterText) => {
  if (!text) return "";
  const beforeTextPattern = cbsLabelPattern(beforeText);
  const afterTextPattern = cbsLabelPattern(afterText);
  const regex = new RegExp(`${beforeTextPattern}(.*?)${afterTextPattern}`, "i");
  const match = text.match(regex);
  return match && match[1] ? match[1].trim() : "";
};

const normalizeCbsAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const numeric = Number(
    String(value)
      .replace(/^\((.*)\)$/, "-$1")
      .replace(/[^0-9.-]/g, ""),
  );

  return Number.isFinite(numeric) ? numeric : null;
};

const extractCbsAmountAfterLabels = (text, labels) => {
  if (!text) return null;

  const normalizedText = String(text).replace(/\s+/g, " ");

  for (const label of labels) {
    const labelPattern = cbsLabelPattern(label);
    const regex = new RegExp(
      `${labelPattern}\\s*(?:\\([^)]*\\)\\s*)?(?:SGD|S\\$)?\\s*([-+]?\\(?\\d[\\d,]*(?:\\.\\d+)?\\)?)`,
      "i",
    );
    const match = normalizedText.match(regex);
    const amount = match?.[1] ? normalizeCbsAmount(match[1]) : null;

    if (amount !== null) {
      return amount;
    }
  }

  return null;
};

const CBS_PRODUCT_TYPES = [
  "Commercial Real Estate",
  "Executive Condominium Equity Withdrawal",
  "Executive Condominium Purchase",
  "Private Residential Equity Withdrawal",
  "Private Residential Purchase",
  "Restructured Mortgage Loan",
  "Secured Bridging Loan",
  "Secured Credit Card",
  "Secured Overdraft",
  "Secured Personal Loan",
  "Secured Restructured Loan",
  "Debt Consolidation Loan",
  "Repayment Assistance Scheme",
  "Unsecured Bridging Loan",
  "Unsecured Credit Card",
  "Unsecured Overdraft",
  "Unsecured Personal Loan",
  "Unsecured Restructured Loan",
  "Unsecured CC",
  "HDB Loan",
  "Margin Trading",
  "Medical Loan",
  "Motor Vehicle Loan",
  "Renovation Loan",
  "Study / Education Loan",
  "Other- Secured",
  "Other- Unsecured",
  "Other",
].sort((left, right) => right.length - left.length);

const CBS_BANK_ALIASES = {
  SCB: "Standard Chartered Bank",
  OCBC: "OCBC",
  UOB: "UOB",
  DBS: "DBS",
  MAYBANK: "Maybank",
};

const CBS_PRODUCT_PATTERN = CBS_PRODUCT_TYPES.map((product) =>
  escapeRegex(product).replace(/\s+/g, "\\s+"),
).join("|");

const extractCbsSection = (text, startLabel, endLabels = []) => {
  if (!text) return "";
  const startIndex = text.search(new RegExp(cbsLabelPattern(startLabel), "i"));
  if (startIndex < 0) return "";

  const remainder = text.slice(startIndex);
  const endIndexes = endLabels
    .map((label) => {
      const matchIndex = remainder
        .slice(1)
        .search(new RegExp(cbsLabelPattern(label), "i"));
      return matchIndex >= 0 ? matchIndex + 1 : -1;
    })
    .filter((index) => index > 0);

  return remainder.slice(0, endIndexes.length > 0 ? Math.min(...endIndexes) : undefined);
};

const getCbsProductAndGrantor = (description) => {
  const cleaned = String(description || "").replace(/\s+/g, " ").trim();
  const product = CBS_PRODUCT_TYPES.find((candidate) =>
    cleaned.toLowerCase().startsWith(candidate.toLowerCase()),
  );

  if (!product) {
    return { product_type: cleaned, grantor_bank: "" };
  }

  const grantor = cleaned.slice(product.length).trim();
  return {
    product_type: product,
    grantor_bank: CBS_BANK_ALIASES[grantor.toUpperCase()] || grantor,
  };
};

const shouldSkipCbsTableLine = (line) =>
  !line ||
  /^Aggregated\s+/i.test(line) ||
  /Month.*Product Type.*Grantor Bank/i.test(line) ||
  /Account\s*Type.*Property/i.test(line) ||
  /^(Month|Product Type|Grantor Bank|Account\s*Type|Secured|Interest Bearing|Non-Interest|Exempted|Balances|Property|Non-Property)$/i.test(
    line,
  ) ||
  /^Page \d+ of \d+$/i.test(line);

const buildCbsTableRow = (descriptor, trailingAmounts, tableType) => {
  const parsed = getCbsProductAndGrantor(descriptor);
  const amounts = trailingAmounts.map(normalizeCbsAmount);
  const rowTotal = amounts.reduce(
    (sum, amount) => sum + (Number.isFinite(amount) ? amount : 0),
    0,
  );

  return {
    key: normalizeCbsTextKey(`${parsed.product_type} ${parsed.grantor_bank}`),
    product_type: parsed.product_type,
    grantor_bank: parsed.grantor_bank,
    detected_from_cbs_table: true,
    [tableType === "outstanding" ? "outstanding_balance" : "monthly_instalment"]:
      rowTotal,
  };
};

const parseCbsCollapsedRows = (section, tableType) => {
  const normalized = String(section || "").replace(/\s+/g, " ").trim();
  const monthMatch = normalized.match(/\b[A-Za-z]+ \d{4}\*?\b/);
  if (!monthMatch) return [];

  const totalIndex = normalized.search(/\bTotal\b/i);
  const body = normalized.slice(
    monthMatch.index,
    totalIndex > monthMatch.index ? totalIndex : undefined,
  );
  const productRegex = new RegExp(`\\b(${CBS_PRODUCT_PATTERN})\\b`, "gi");
  const productMatches = [];
  let productMatch;

  while ((productMatch = productRegex.exec(body)) !== null) {
    productMatches.push({
      index: productMatch.index,
      product: productMatch[0],
    });
  }

  return productMatches
    .map((match, index) => {
      const nextMatch = productMatches[index + 1];
      const rowText = body.slice(match.index, nextMatch?.index).trim();
      const amountMatches = rowText.match(/[-+]?\.?\d[\d,]*(?:\.\d+)?/g) || [];
      if (amountMatches.length < 4) return null;

      const trailingAmounts = amountMatches.slice(-4);
      const trailingPattern = trailingAmounts.map(escapeRegex).join("\\s+");
      const rowMatch = rowText.match(new RegExp(`^(.*?)\\s+${trailingPattern}\\s*$`));
      if (!rowMatch?.[1]) return null;

      const descriptor = rowMatch[1]
        .replace(/^[A-Za-z]+ \d{4}\*?\s+/, "")
        .replace(/\s+(Single|Joint|All|-)\s*$/i, "")
        .trim();

      return buildCbsTableRow(descriptor, trailingAmounts, tableType);
    })
    .filter(Boolean);
};

const parseCbsLatestMonthRows = (section, tableType) => {
  const lines = String(section || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const rows = [];
  let buffer = "";
  let hasStarted = false;

  for (const line of lines) {
    if (/^Total\b/i.test(line) || /Preceding\s+5\s+Months/i.test(line)) break;
    if (shouldSkipCbsTableLine(line)) continue;
    if (!hasStarted && !/^[A-Za-z]+ \d{4}\*?\b/.test(line)) continue;
    hasStarted = true;

    buffer = `${buffer} ${line}`.trim();
    const amountMatches = buffer.match(/[-+]?\.?\d[\d,]*(?:\.\d+)?/g) || [];
    if (amountMatches.length < 4) continue;

    const trailingAmounts = amountMatches.slice(-4);
    const trailingPattern = trailingAmounts
      .map((amount) => amount.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("\\s+");
    const rowMatch = buffer.match(new RegExp(`^(.*?)\\s+${trailingPattern}\\s*$`));
    if (!rowMatch?.[1]) continue;

    let descriptor = rowMatch[1]
      .replace(/^[A-Za-z]+ \d{4}\*?\s+/, "")
      .replace(/\s+(Single|Joint|All|-)\s*$/i, "")
      .trim();
    rows.push(buildCbsTableRow(descriptor, trailingAmounts, tableType));

    buffer = "";
  }

  return [...rows, ...parseCbsCollapsedRows(section, tableType)];
};

const sumCbsTrailingAmounts = (amounts) =>
  amounts.reduce((sum, amount) => {
    const numeric = normalizeCbsAmount(amount);
    return sum + (Number.isFinite(numeric) ? numeric : 0);
  }, 0);

const parseCbsMonthlyInstalmentHistory = (text) => {
  const section = extractCbsSection(text, "Aggregated Monthly Instalment", [
    "Disclaimer",
    "End Of Report",
  ]);
  const normalized = String(section || "").replace(/\s+/g, " ").trim();
  if (!normalized) return [];

  const rows = [];
  const latestMonth = normalized.match(/\b([A-Za-z]+ \d{4})\*?\b/)?.[1] || null;
  const totalMatch = normalized.match(
    /\bTotal\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)/i,
  );

  if (latestMonth && totalMatch) {
    rows.push({
      month_label: latestMonth,
      monthly_instalment: sumCbsTrailingAmounts(totalMatch.slice(1, 5)),
    });
  }

  const precedingSection = extractCbsSection(
    section,
    "Aggregated Monthly Instalments for Preceding 5 Months",
    ["* The", "Disclaimer", "End Of Report"],
  ).replace(/^Aggregated Monthly Instalments for Preceding 5 Months/i, "");
  const precedingNormalized = precedingSection.replace(/\s+/g, " ").trim();
  const precedingRegex =
    /\b([A-Za-z]+ \d{4})\s+All\s+All(?:\s+All|\s+-)?\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)/gi;
  let match;

  while ((match = precedingRegex.exec(precedingNormalized)) !== null) {
    const amountValues = match.slice(2, 6).filter((value) => !/^NA$/i.test(value));
    rows.push({
      month_label: match[1],
      monthly_instalment: sumCbsTrailingAmounts(amountValues),
    });
  }

  return rows.slice(0, 6).reverse();
};

const extractCbsObligations = (text) => {
  const outstandingRows = parseCbsLatestMonthRows(
    extractCbsSection(text, "Aggregated Outstanding Balances", [
      "Aggregated Outstanding Balances for Preceding 5 Months",
      "Aggregated Monthly Instalment",
    ]),
    "outstanding",
  );
  const instalmentRows = parseCbsLatestMonthRows(
    extractCbsSection(text, "Aggregated Monthly Instalment", [
      "Aggregated Monthly Instalments for Preceding 5 Months",
      "Disclaimer",
      "End Of Report",
    ]),
    "instalment",
  );
  const rowsByKey = new Map();

  outstandingRows.forEach((row) => {
    rowsByKey.set(row.key, { source: "CBS", ...row });
  });
  instalmentRows.forEach((row) => {
    rowsByKey.set(row.key, {
      source: "CBS",
      ...(rowsByKey.get(row.key) || {}),
      ...row,
    });
  });

  const granularRows = Array.from(rowsByKey.values())
    .filter(
      (row) =>
        row.product_type &&
        row.grantor_bank &&
        row.detected_from_cbs_table,
    )
    .map((row) => ({
      ...row,
      facility: row.product_type,
    }));

  if (granularRows.length > 0) {
    return granularRows;
  }

  const outstandingBalance = extractCbsAmountAfterLabels(
    text,
    [
      "Aggregated Outstanding Balances",
      "Aggregated Outstanding Balance",
      "Aggregate Outstanding Balances",
      "Aggregate Outstanding Balance",
      "Total Outstanding Balances",
      "Total Outstanding Balance",
    ],
  );
  const monthlyInstalment = extractCbsAmountAfterLabels(
    text,
    [
      "Aggregated Monthly Instalment",
      "Aggregated Monthly Instalments",
      "Aggregated Monthly Installment",
      "Aggregated Monthly Installments",
      "Aggregate Monthly Instalment",
      "Aggregate Monthly Instalments",
      "Total Monthly Instalment",
      "Total Monthly Instalments",
    ],
  );

  if (outstandingBalance === null && monthlyInstalment === null) {
    return [];
  }

  return [
    {
      source: "CBS",
      facility: "Aggregated CBS facilities",
      outstanding_balance: outstandingBalance,
      monthly_instalment: monthlyInstalment,
    },
  ];
};

const extractCbsLatestTotals = (text, startLabel, endLabels = []) => {
  const section = extractCbsSection(text, startLabel, endLabels);
  const normalized = String(section || "").replace(/\s+/g, " ").trim();
  const totalMatch = normalized.match(
    /\bTotal\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)/i,
  );

  if (!totalMatch) return [];

  return totalMatch.slice(1, 5).map((value) =>
    /^NA$/i.test(value) ? null : normalizeCbsAmount(value),
  );
};

const parseCbsOutstandingHistory = (text) => {
  const section = extractCbsSection(
    text,
    "Aggregated Outstanding Balances for Preceding 5 Months",
    ["Aggregated Monthly Instalment", "Disclaimer", "End Of Report"],
  );
  const normalized = String(section || "").replace(/\s+/g, " ").trim();
  if (!normalized) return [];

  const rows = [];
  const precedingRegex =
    /\b([A-Za-z]+ \d{4})\s+All\s+All(?:\s+All|\s+-)?\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)/gi;
  let match;

  while ((match = precedingRegex.exec(normalized)) !== null) {
    const amountValues = match.slice(2, 6).filter((value) => !/^NA$/i.test(value));
    rows.push({
      date: match[1],
      value: String(sumCbsTrailingAmounts(amountValues)),
    });
  }

  return rows.slice(0, 5).reverse();
};

const formatCbsCurrencyValue = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? null
    : Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "SGD",
      });

const formatCbsPercentValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric.toFixed(2);
};

const normalizeCbsRiskGrade = (value) => {
  const match = String(value || "").trim().match(/^([A-H]{2})/i);
  return match ? match[1].toUpperCase() : String(value || "").trim();
};

const getImportantCbsRows = (rows = []) =>
  rows.filter((row) =>
    /private residential purchase|motor vehicle loan|hdb loan/i.test(
      `${row.product_type || ""} ${row.facility || ""}`,
    ),
  );

const parseCbsData = (text) => {
  if (!text) return null;

  const securedCreditLimit = extractTextBetween(
    text,
    "Secured Credit Limit",
    "Unsecured Credit Limit",
  );
  const unsecuredCreditLimit = extractTextBetween(
    text,
    "Unsecured Credit Limit",
    "Exempted Credit Limit",
  );
  const creditScore = extractTextBetween(
    text,
    "financial institution.  Score",
    "Risk Grade",
  );
  const riskGrade = extractTextBetween(
    text,
    "Risk Grade ",
    "Risk Grade Description",
  );
  const probabilityDefault = extractTextBetween(
    text,
    "Probability of Default ",
    " For more details on the description",
  );
  const cbsObligations = extractCbsObligations(text);
  const cbsMonthlyInstalments = parseCbsMonthlyInstalmentHistory(text);
  const normalizedRiskGrade = normalizeCbsRiskGrade(riskGrade);
  const outstandingTotals = extractCbsLatestTotals(
    text,
    "Aggregated Outstanding Balances",
    [
      "Aggregated Outstanding Balances for Preceding 5 Months",
      "Aggregated Monthly Instalment",
    ],
  );
  const instalmentTotals = extractCbsLatestTotals(
    text,
    "Aggregated Monthly Instalment",
    [
      "Aggregated Monthly Instalments for Preceding 5 Months",
      "Disclaimer",
      "End Of Report",
    ],
  );
  const securedLimit = normalizeCbsAmount(securedCreditLimit);
  const unsecuredLimit = normalizeCbsAmount(unsecuredCreditLimit);
  const securedUtilized = outstandingTotals[0] ?? null;
  const unsecuredUtilized =
    (outstandingTotals[1] ?? 0) + (outstandingTotals[2] ?? 0) || null;
  const monthlyCommitment =
    (instalmentTotals[0] ?? 0) + (instalmentTotals[1] ?? 0) || null;
  const importantRows = getImportantCbsRows(cbsObligations);
  const outstandingHistory = parseCbsOutstandingHistory(text);
  const defaults = normalizeCbsAmount(
    extractTextBetween(text, "Defaults", "Bankruptcy Proceedings"),
  );
  const bankruptcyProceedings = normalizeCbsAmount(
    extractTextBetween(text, "Bankruptcy Proceedings", "Secured Credit Limit"),
  );
  const overdueBalance = normalizeCbsAmount(
    extractTextBetween(text, "Overdue Balance", "Defaults"),
  );

  const payload = {
    secured_credit_limit: securedCreditLimit || null,
    unsecured_credit_limit: unsecuredCreditLimit || null,
    credit_score: creditScore || null,
    risk_grade: normalizedRiskGrade || null,
    probability_default: probabilityDefault || null,
    cbs_obligations: cbsObligations,
    cbs_monthly_instalment_history: cbsMonthlyInstalments,
    EnquiryDate: extractCbsEnquiryDate(text),
    SecuredCreditLimit: securedLimit,
    UnsecuredCreditLimit: unsecuredLimit,
    CreditScore: normalizeCbsAmount(creditScore),
    RiskGrade: normalizedRiskGrade || null,
    ProbabilityofDefault: normalizeCbsAmount(probabilityDefault),
    SecuredCreditUtilized: securedUtilized,
    UnsecuredCreditUtilized: unsecuredUtilized,
    SecuredCreditUtilizationinpercentage:
      securedLimit ? formatCbsPercentValue((securedUtilized / securedLimit) * 100) : null,
    UnsecuredCreditUtilizationinpercentage:
      unsecuredLimit
        ? formatCbsPercentValue((unsecuredUtilized / unsecuredLimit) * 100)
        : null,
    MonthlyCommitment: formatCbsCurrencyValue(monthlyCommitment),
    NoteworthyOngoingLoans: importantRows.length
      ? importantRows.map((row) =>
          [
            row.product_type || row.facility || "CBS facility",
            row.grantor_bank || "",
            row.outstanding_balance ?? "",
            row.monthly_instalment ?? "",
          ]
            .filter((value) => value !== "")
            .join("   "),
        )
      : null,
    Last6MonthsGraphChartMaxData: outstandingHistory.length
      ? Math.max(...outstandingHistory.map((row) => Number(row.value) || 0))
      : null,
    Last6MonthsGraphChartData: outstandingHistory.length ? outstandingHistory : null,
    CBSImpLoanSupplementaryInstallment: importantRows.length
      ? importantRows.map((row) =>
          [
            row.product_type || row.facility || "CBS facility",
            row.grantor_bank || "",
            row.monthly_instalment ?? "",
          ]
            .filter((value) => value !== "")
            .join("   "),
        )
      : null,
    defaults,
    bankcruptcyProceedings: bankruptcyProceedings,
    overdueBalance,
  };

  return Object.entries(payload).some(([key, value]) =>
    ["cbs_obligations", "cbs_monthly_instalment_history"].includes(key)
      ? value.length > 0
      : Boolean(value),
  )
    ? payload
    : null;
};

const getCbsObligationRows = (cbsReports) =>
  (cbsReports || []).flatMap((report) =>
    (report.parsed_payload?.cbs_obligations || []).map((obligation, index) => ({
      ...obligation,
      id: `${report.id || "cbs"}-${index}`,
      report_name: report.original_file_name,
      enquiry_date: report.parsed_payload?.enquiry_date,
    })),
  );

const getCbsMonthlyInstalmentHistories = (cbsReports) =>
  (cbsReports || [])
    .map((report) => ({
      id: report.id || report.original_file_name,
      report_name: report.original_file_name,
      enquiry_date: report.parsed_payload?.enquiry_date,
      rows: report.parsed_payload?.cbs_monthly_instalment_history || [],
    }))
    .filter((report) => report.rows.length > 0);

const extractCbsEnquiryDate = (text) => {
  if (!text) return null;
  const enquirySegmentMatch = text.match(/Enquiry\s+Date\s*(.*?)\s*Reference/i);
  if (!enquirySegmentMatch?.[1]) return null;
  const dateMatch = enquirySegmentMatch[1].match(/\b\d{2}\/\d{2}\/\d{4}\b/);
  return dateMatch?.[0] || null;
};

const defaultProgress = {
  total: 0,
  completed: 0,
  processing: 0,
  queued: 0,
  percent: 0,
};

const progressFromSubmissionDetails = (submissions = [], statements = []) => {
  if (submissions.length > 0) {
    const progress = submissions.reduce(
      (current, submission) => {
        const status = String(submission?.status || "").toLowerCase();

        if (status === "completed" || submission?.has_analysis) {
          current.completed += 1;
        } else if (status === "processing") {
          current.processing += 1;
        } else {
          current.queued += 1;
        }

        return current;
      },
      {
        total: submissions.length,
        completed: 0,
        processing: 0,
        queued: 0,
        percent: 0,
      },
    );

    progress.percent =
      progress.total > 0
        ? Math.round((progress.completed / progress.total) * 100)
        : 0;

    return progress;
  }

  if (statements.length > 0) {
    return {
      total: statements.length,
      completed: statements.length,
      processing: 0,
      queued: 0,
      percent: 100,
    };
  }

  return null;
};

const mergeGroupProgressFromDetails = (group, submissions = [], statements = []) => {
  if (!group) {
    return group;
  }

  const currentTotal = Number(group?.progress?.total || 0);
  const detailProgress = progressFromSubmissionDetails(submissions, statements);

  if (!detailProgress || currentTotal > 0) {
    return group;
  }

  return {
    ...group,
    progress: detailProgress,
    status:
      detailProgress.total > 0 && detailProgress.completed === detailProgress.total
        ? "completed"
        : detailProgress.completed > 0 || detailProgress.processing > 0
          ? "processing"
          : "queued",
  };
};

const preserveKnownProgress = (incomingGroups = [], currentGroups = []) =>
  incomingGroups.map((incomingGroup) => {
    const incomingTotal = Number(incomingGroup?.progress?.total || 0);
    if (incomingTotal > 0) {
      return incomingGroup;
    }

    const currentGroup = currentGroups.find(
      (group) => String(group.id) === String(incomingGroup.id),
    );
    const currentProgress = currentGroup?.progress;
    const currentTotal = Number(currentProgress?.total || 0);

    if (currentTotal <= 0) {
      return incomingGroup;
    }

    return {
      ...incomingGroup,
      progress: currentProgress,
      status: currentGroup.status,
    };
  });

const createEmptyGroupDetailState = (overrides = {}) => ({
  loading: false,
  error: null,
  statementsLoading: false,
  statementsError: null,
  reportLoading: false,
  reportError: null,
  group: null,
  submissions: [],
  cbsReports: [],
  mlcbReports: [],
  statements: [],
  report: null,
  questionAssistant: null,
  ...overrides,
});

const statusPillStyle = (status) => {
  if (status === "completed") {
    return {
      background: "rgba(17, 121, 73, 0.12)",
      color: "#117949",
    };
  }

  if (status === "processing") {
    return {
      background: "rgba(14, 90, 165, 0.12)",
      color: "#0e5aa5",
    };
  }

  if (status === "queued") {
    return {
      background: "rgba(196, 123, 13, 0.12)",
      color: "#9c6500",
    };
  }

  return {
    background: "rgba(97, 116, 135, 0.12)",
    color: "#617487",
  };
};

const fraudRiskBand = (score, level) => {
  const normalizedLevel = String(level || "").toLowerCase();
  if (["critical", "high"].includes(normalizedLevel)) return "high";
  if (["medium", "moderate"].includes(normalizedLevel)) return "medium";
  if (["low", "none", "clear"].includes(normalizedLevel)) return "low";

  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) return "unknown";
  if (numericScore >= 70) return "high";
  if (numericScore >= 35) return "medium";
  return "low";
};

const fraudRiskPillStyle = (score, level) => {
  const band = fraudRiskBand(score, level);

  if (band === "high") {
    return {
      background: "rgba(194, 56, 74, 0.12)",
      color: "#c2384a",
      border: "1px solid rgba(194, 56, 74, 0.18)",
    };
  }

  if (band === "medium") {
    return {
      background: "rgba(196, 123, 13, 0.12)",
      color: "#9c6500",
      border: "1px solid rgba(196, 123, 13, 0.18)",
    };
  }

  if (band === "low") {
    return {
      background: "rgba(17, 121, 73, 0.12)",
      color: "#117949",
      border: "1px solid rgba(17, 121, 73, 0.18)",
    };
  }

  return {
    background: "rgba(97, 116, 135, 0.12)",
    color: "#617487",
    border: "1px solid rgba(97, 116, 135, 0.18)",
  };
};

const PortalDashboard = () => {
  const history = useHistory();
  const token = Cookies.get("bank_statement_portal_token");
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1440 : window.innerWidth,
  );
  const [me, setMe] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupDetailState, setGroupDetailState] = useState(() =>
    createEmptyGroupDetailState(),
  );
  const [pageError, setPageError] = useState("");
  const [groupError, setGroupError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [cbsUploadError, setCbsUploadError] = useState("");
  const [mlcbUploadError, setMlcbUploadError] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isCbsUploading, setIsCbsUploading] = useState(false);
  const [isMlcbUploading, setIsMlcbUploading] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isResettingGroupJobs, setIsResettingGroupJobs] = useState(false);
  const [isSavingReportScenario, setIsSavingReportScenario] = useState(false);
  const [mobileView, setMobileView] = useState("groups");
  const [reportLoadingMessageIndex, setReportLoadingMessageIndex] = useState(0);
  const reportRef = useRef(null);
  const combinedAnalysisRef = useRef(null);
  const groupDetailRequestRef = useRef(0);
  const groupDetailStateRef = useRef(groupDetailState);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${Cookies.get("bank_statement_portal_token")}`,
      Accept: "application/json",
    }),
    [],
  );

  const handleUnauthorized = useCallback(() => {
    Cookies.remove("bank_statement_portal_token");
    Cookies.remove("bank_statement_portal_name");
    history.replace("/bank-statement-portal/login");
  }, [history]);

  useEffect(() => {
    groupDetailStateRef.current = groupDetailState;
  }, [groupDetailState]);

  useEffect(() => {
    if (!groupDetailState.reportLoading) {
      setReportLoadingMessageIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setReportLoadingMessageIndex(
        (currentIndex) => (currentIndex + 1) % REPORT_LOADING_MESSAGES.length,
      );
    }, 4500);

    return () => window.clearInterval(intervalId);
  }, [groupDetailState.reportLoading]);

  const groupedAnalysisStatements = useMemo(() => {
    const reportGroups = buildGroupedAnalysisStatementsFromReport(
      groupDetailState.report,
    );

    if (reportGroups.length > 0) {
      return reportGroups;
    }

    const groupedStatements = new Map();

    (groupDetailState.statements || []).forEach((statement) => {
      const bank = normalizeStatementGroupValue(statement?.bank, "Unknown Bank");
      const accountNumber = normalizeStatementGroupValue(
        statement?.bank_account_number,
        "-",
      );
      const groupKey = `${bank.toLowerCase()}::${accountNumber.toLowerCase()}`;

      if (!groupedStatements.has(groupKey)) {
        groupedStatements.set(groupKey, {
          groupKey,
          bank,
          accountNumber,
          statements: [],
        });
      }

      groupedStatements.get(groupKey).statements.push(statement);
    });

    return Array.from(groupedStatements.values())
      .map((group) => ({
        ...group,
        statements: [...group.statements].sort(compareBankStatementsByMonth),
      }))
      .sort((left, right) => {
        const bankDiff = left.bank.localeCompare(right.bank);
        if (bankDiff !== 0) return bankDiff;
        return left.accountNumber.localeCompare(right.accountNumber);
      });
  }, [groupDetailState.report, groupDetailState.statements]);

  const selectedGroup = useMemo(() => {
    if (
      groupDetailState.group &&
      String(groupDetailState.group.id) === String(selectedGroupId)
    ) {
      return mergeGroupProgressFromDetails(
        groupDetailState.group,
        groupDetailState.submissions,
        groupDetailState.statements,
      );
    }

    return (
      groups.find((group) => String(group.id) === String(selectedGroupId)) || null
    );
  }, [
    groupDetailState.group,
    groupDetailState.statements,
    groupDetailState.submissions,
    groups,
    selectedGroupId,
  ]);

  const selectedGroupSubmissions =
    String(groupDetailState.group?.id) === String(selectedGroupId)
      ? groupDetailState.submissions
      : [];
  const selectedGroupProgress =
    progressFromSubmissionDetails(
      selectedGroupSubmissions,
      String(groupDetailState.group?.id) === String(selectedGroupId)
        ? groupDetailState.statements
        : [],
    ) ||
    selectedGroup?.progress ||
    defaultProgress;
  const canResetGroupJobs = Boolean(me?.is_admin_test_login);
  const fraudChecksByJobId = useMemo(() => {
    const checks = new Map();

    (groupDetailState.report?.fraud_checks || []).forEach((item) => {
      if (item?.job_id !== undefined && item?.job_id !== null) {
        checks.set(String(item.job_id), item);
      }
    });

    return checks;
  }, [groupDetailState.report]);
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth < 1100;
  const reportLoadingMessage =
    REPORT_LOADING_MESSAGES[
      reportLoadingMessageIndex % REPORT_LOADING_MESSAGES.length
    ];
  const filteredGroups = useMemo(() => {
    const normalizedQuery = groupSearchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return groups;
    }

    return groups.filter((group) =>
      String(group?.name || "").toLowerCase().includes(normalizedQuery),
    );
  }, [groups, groupSearchQuery]);

  const hasProcessingGroups = useMemo(
    () =>
      groups.some((group) => {
        const total = Number(group?.progress?.total || 0);
        const completed = Number(group?.progress?.completed || 0);
        return total > 0 && completed < total;
      }),
    [groups],
  );

  const hasProcessingMlcbReports = useMemo(
    () =>
      (groupDetailState.mlcbReports || []).some(
        (report) => report?.status === "processing",
      ),
    [groupDetailState.mlcbReports],
  );

  useEffect(() => {
    if (!token) {
      history.replace("/bank-statement-portal/login");
    }
  }, [history, token]);

  const loadDashboard = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setIsLoading(true);
        setPageError("");
      } else {
        setIsPolling(true);
      }

      try {
        const [meResponse, groupsResponse] = await Promise.all([
          fetch(`${API_BASE}/bank-statement-portal/me`, {
            headers: authHeaders,
          }),
          fetch(`${API_BASE}/bank-statement-portal/groups`, {
            headers: authHeaders,
          }),
        ]);

        if (meResponse.status === 401 || groupsResponse.status === 401) {
          handleUnauthorized();
          return;
        }

        const meJson = await meResponse.json();
        const groupsJson = await groupsResponse.json();

        if (!meResponse.ok) {
          throw new Error(meJson?.message || "Unable to load portal account.");
        }

        if (!groupsResponse.ok) {
          throw new Error(groupsJson?.message || "Unable to load groups.");
        }

        const nextGroups = groupsJson.groups || [];

        setMe(meJson.user);
        setGroups((currentGroups) =>
          preserveKnownProgress(nextGroups, currentGroups),
        );
        setSelectedGroupId((current) => {
          if (
            current &&
            nextGroups.some((group) => String(group.id) === String(current))
          ) {
            return current;
          }

          return viewportWidth < 768 ? null : nextGroups?.[0]?.id || null;
        });
      } catch (error) {
        if (!silent) {
          setPageError(error.message);
        }
      } finally {
        if (!silent) {
          setIsLoading(false);
        } else {
          setIsPolling(false);
        }
      }
    },
    [authHeaders, handleUnauthorized, viewportWidth],
  );

  const loadGroupDetail = useCallback(
    async (groupId, { silent = false } = {}) => {
      const requestId = groupDetailRequestRef.current + 1;
      groupDetailRequestRef.current = requestId;

      if (!groupId) {
        setGroupDetailState(createEmptyGroupDetailState());
        return;
      }

      if (!silent) {
        setGroupDetailState(createEmptyGroupDetailState({
          loading: true,
        }));
      }

      try {
        const response = await fetch(
          `${API_BASE}/bank-statement-portal/groups/${groupId}?lazy=1`,
          { headers: authHeaders },
        );

        if (response.status === 401) {
          handleUnauthorized();
          return;
        }

        const json = await readJsonResponse(response);
        if (!response.ok) {
          throw new Error(
            responseErrorMessage(response, json, "Unable to load group analysis."),
          );
        }

        if (groupDetailRequestRef.current !== requestId) {
          return;
        }

        const nextSubmissions = json.submissions || [];
        const completedSubmissions = nextSubmissions.filter(
          (submission) =>
            submission?.id &&
            (submission?.has_analysis ||
              String(submission?.status || "").toLowerCase() === "completed"),
        );
        const currentGroupDetailState = groupDetailStateRef.current;
        const existingStatements =
          silent &&
          String(currentGroupDetailState.group?.id) === String(groupId)
            ? currentGroupDetailState.statements || []
            : [];
        const loadedJobIds = new Set(
          existingStatements
            .map((statement) => statement?.job_id)
            .filter((jobId) => jobId !== undefined && jobId !== null)
            .map((jobId) => String(jobId)),
        );
        const submissionsToLoad = completedSubmissions.filter((submission) => {
          const jobId = submission?.bank_document_job_id;
          return jobId === undefined || jobId === null
            ? true
            : !loadedJobIds.has(String(jobId));
        });

        setGroupDetailState({
          loading: false,
          error: null,
          statementsLoading: submissionsToLoad.length > 0,
          statementsError: null,
          reportLoading: completedSubmissions.length > 0,
          reportError: null,
          group: json.group || null,
          submissions: nextSubmissions,
          cbsReports: json.cbs_reports || [],
          mlcbReports: json.mlcb_reports || [],
          statements: existingStatements,
          report:
            silent && String(currentGroupDetailState.group?.id) === String(groupId)
              ? currentGroupDetailState.report
              : null,
          questionAssistant:
            silent && String(currentGroupDetailState.group?.id) === String(groupId)
              ? currentGroupDetailState.questionAssistant
              : null,
        });

        if (json.group) {
          const groupWithDetailProgress = mergeGroupProgressFromDetails(
            json.group,
            json.submissions || [],
            json.statements || [],
          );

          setGroups((currentGroups) =>
            currentGroups.map((group) =>
              String(group.id) === String(json.group.id)
                ? groupWithDetailProgress
                : group,
            ),
          );
        }

        if (completedSubmissions.length > 0) {
          void (async () => {
            try {
              const reportResponse = await fetch(
                `${API_BASE}/bank-statement-portal/groups/${groupId}/report`,
                { headers: authHeaders },
              );

              if (reportResponse.status === 401) {
                handleUnauthorized();
                return;
              }

              const reportJson = await readJsonResponse(reportResponse);
              if (!reportResponse.ok) {
                throw new Error(
                  responseErrorMessage(
                    reportResponse,
                    reportJson,
                    "Unable to load lending report.",
                  ),
                );
              }

              if (!reportJson || groupDetailRequestRef.current !== requestId) {
                return;
              }

              setGroupDetailState((current) => {
                if (String(current.group?.id) !== String(groupId)) {
                  return current;
                }

                return {
                  ...current,
                  reportLoading: Boolean(reportJson.full_report_available),
                  reportError: null,
                  report: reportJson.report || null,
                  questionAssistant: reportJson.question_assistant || null,
                };
              });

              if (!reportJson.full_report_available) {
                return;
              }

              try {
                const fullReportResponse = await fetch(
                  `${API_BASE}/bank-statement-portal/groups/${groupId}/report?full=1&include_transaction_picker=1`,
                  { headers: authHeaders },
                );

                if (fullReportResponse.status === 401) {
                  handleUnauthorized();
                  return;
                }

                const fullReportJson = await readJsonResponse(fullReportResponse);
                if (!fullReportResponse.ok) {
                  throw new Error(
                    responseErrorMessage(
                      fullReportResponse,
                      fullReportJson,
                      "Unable to load full lending report.",
                    ),
                  );
                }

                if (!fullReportJson || groupDetailRequestRef.current !== requestId) {
                  return;
                }

                setGroupDetailState((current) => {
                  if (String(current.group?.id) !== String(groupId)) {
                    return current;
                  }

                  return {
                    ...current,
                    reportLoading: false,
                    reportError: null,
                    report: fullReportJson.report || current.report,
                    questionAssistant:
                      fullReportJson.question_assistant ||
                      current.questionAssistant,
                  };
                });
              } catch (fullReportError) {
                if (groupDetailRequestRef.current !== requestId) {
                  return;
                }

                setGroupDetailState((current) => {
                  if (String(current.group?.id) !== String(groupId)) {
                    return current;
                  }

                  return {
                    ...current,
                    reportLoading: false,
                    reportError:
                      fullReportError.message ||
                      "Full transaction-level report is unavailable.",
                  };
                });
              }
            } catch (error) {
              if (groupDetailRequestRef.current !== requestId) {
                return;
              }

              setGroupDetailState((current) => {
                if (String(current.group?.id) !== String(groupId)) {
                  return current;
                }

                return {
                  ...current,
                  reportLoading: false,
                  reportError: error.message || "Unable to load lending report.",
                  report: null,
                  questionAssistant: null,
                };
              });
            }
          })();
        }

        for (const submission of submissionsToLoad) {
          if (groupDetailRequestRef.current !== requestId) {
            return;
          }

          try {
            const submissionResponse = await fetch(
              `${API_BASE}/bank-statement-portal/submissions/${submission.id}`,
              { headers: authHeaders },
            );

            if (submissionResponse.status === 401) {
              handleUnauthorized();
              return;
            }

            const submissionJson = await readJsonResponse(submissionResponse);
            if (!submissionResponse.ok) {
              throw new Error(
                responseErrorMessage(
                  submissionResponse,
                  submissionJson,
                  "Unable to load statement analysis.",
                ),
              );
            }

            if (groupDetailRequestRef.current !== requestId) {
              return;
            }

            setGroupDetailState((current) => {
              if (String(current.group?.id) !== String(groupId)) {
                return current;
              }

              const nextStatements = [...(current.statements || [])];
              (submissionJson.statements || []).forEach((statement) => {
                const statementJobId = statement?.job_id;
                const existingIndex = nextStatements.findIndex(
                  (item) =>
                    statementJobId !== undefined &&
                    statementJobId !== null &&
                    String(item?.job_id) === String(statementJobId),
                );

                if (existingIndex >= 0) {
                  nextStatements[existingIndex] = statement;
                } else {
                  nextStatements.push(statement);
                }
              });

              return {
                ...current,
                statements: nextStatements,
                statementsError: null,
              };
            });
          } catch (error) {
            if (groupDetailRequestRef.current !== requestId) {
              return;
            }

            setGroupDetailState((current) => {
              if (String(current.group?.id) !== String(groupId)) {
                return current;
              }

              return {
                ...current,
                statementsError:
                  error.message || "Unable to load statement analysis.",
              };
            });
          }
        }

        if (groupDetailRequestRef.current === requestId) {
          setGroupDetailState((current) =>
            String(current.group?.id) === String(groupId)
              ? { ...current, statementsLoading: false }
              : current,
          );
        }
      } catch (error) {
        if (groupDetailRequestRef.current !== requestId) {
          return;
        }

        setGroupDetailState(createEmptyGroupDetailState({
          loading: false,
          error: error.message,
        }));
      }
    },
    [authHeaders, handleUnauthorized],
  );

  const saveReportScenario = useCallback(
    async (scenario) => {
      if (!selectedGroupId) {
        return;
      }

      setIsSavingReportScenario(true);

      try {
        const response = await fetch(
          `${API_BASE}/bank-statement-portal/groups/${selectedGroupId}/report-scenario`,
          {
            method: "PUT",
            headers: {
              ...authHeaders,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ scenario }),
          },
        );

        if (response.status === 401) {
          handleUnauthorized();
          return;
        }

        const json = await readJsonResponse(response);
        if (!response.ok) {
          throw new Error(
            responseErrorMessage(response, json, "Unable to save report choices."),
          );
        }

        if (json.group) {
          setGroupDetailState((current) => ({
            ...current,
            group: json.group,
          }));
          setGroups((currentGroups) =>
            currentGroups.map((group) =>
              String(group.id) === String(json.group.id) ? json.group : group,
            ),
          );
        }
      } catch (error) {
        setGroupDetailState((current) => ({
          ...current,
          error: error.message,
        }));
      } finally {
        setIsSavingReportScenario(false);
      }
    },
    [authHeaders, handleUnauthorized, selectedGroupId],
  );

  useEffect(() => {
    if (token) {
      loadDashboard();
    }
  }, [loadDashboard, token]);

  useEffect(() => {
    if (!selectedGroupId) {
      setGroupDetailState(createEmptyGroupDetailState());
      return;
    }

    loadGroupDetail(selectedGroupId);
  }, [loadGroupDetail, selectedGroupId]);

  useEffect(() => {
    if (!token || (!hasProcessingGroups && !hasProcessingMlcbReports)) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadDashboard({ silent: true });
      if (selectedGroupId) {
        loadGroupDetail(selectedGroupId, { silent: true });
      }
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    hasProcessingGroups,
    hasProcessingMlcbReports,
    loadDashboard,
    loadGroupDetail,
    selectedGroupId,
    token,
  ]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile && mobileView === "group" && !selectedGroupId) {
      setMobileView("groups");
    }
  }, [isMobile, mobileView, selectedGroupId]);

  const handleSelectGroup = useCallback(
    (groupId) => {
      setSelectedGroupId(groupId);
      if (isMobile) {
        setMobileView("group");
      }
    },
    [isMobile],
  );

  const handleCreateGroup = async (event) => {
    event.preventDefault();

    const trimmedName = groupName.trim();
    if (!trimmedName) {
      setGroupError("Please name the group before creating it.");
      return;
    }

    setIsCreatingGroup(true);
    setGroupError("");

    try {
      const response = await fetch(`${API_BASE}/bank-statement-portal/groups`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: trimmedName }),
      });

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(responseErrorMessage(response, json, "Unable to create group."));
      }

      setGroups((current) => [json.group, ...current]);
      setSelectedGroupId(json.group?.id || null);
      setGroupDetailState(createEmptyGroupDetailState({
        group: json.group || null,
        submissions: json.group?.submissions || [],
      }));
      setGroupName("");
      if (isMobile) {
        setMobileView("group");
      }
    } catch (error) {
      setGroupError(error.message);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (files.length === 0) {
      return;
    }

    if (!selectedGroupId) {
      setUploadError("Create or select a group before uploading statements.");
      return;
    }

    const sizeError = validatePortalUploadSize(files, "bank statement");
    if (sizeError) {
      setUploadError(sizeError);
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("group_id", String(selectedGroupId));
      files.forEach((file) => formData.append("files[]", file));

      const response = await fetch(`${API_BASE}/bank-statement-portal/submissions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Cookies.get("bank_statement_portal_token")}`,
        },
        body: formData,
      });

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(
          responseErrorMessage(response, json, "Unable to upload documents."),
        );
      }

      setMe((current) =>
        current
          ? { ...current, tokens_remaining: json.tokens_remaining }
          : current,
      );

      if (json.group) {
        setSelectedGroupId(json.group.id);
        if (isMobile) {
          setMobileView("group");
        }
      }

      await Promise.all([
        loadDashboard({ silent: true }),
        loadGroupDetail(json.group?.id || selectedGroupId, { silent: true }),
      ]);
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadSupplementaryDocument = useCallback(
    async ({ file, documentType, parsedPayload = null }) => {
      const formData = new FormData();
      formData.append("group_id", String(selectedGroupId));
      formData.append("document_type", documentType);
      formData.append("file", file);

      if (parsedPayload) {
        formData.append("parsed_payload", JSON.stringify(parsedPayload));
      }

      const response = await fetch(`${API_BASE}/bank-statement-portal/submissions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Cookies.get("bank_statement_portal_token")}`,
        },
        body: formData,
      });

      if (response.status === 401) {
        handleUnauthorized();
        return null;
      }

      const json = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(
          responseErrorMessage(response, json, "Unable to upload document."),
        );
      }

      await Promise.all([
        loadDashboard({ silent: true }),
        loadGroupDetail(json.group?.id || selectedGroupId, { silent: true }),
      ]);

      return json;
    },
    [handleUnauthorized, loadDashboard, loadGroupDetail, selectedGroupId],
  );

  const handleCbsUpload = async (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!selectedGroupId) {
      setCbsUploadError("Create or select a group before uploading a CBS report.");
      return;
    }

    const sizeError = validatePortalUploadSize([file], "CBS report");
    if (sizeError) {
      setCbsUploadError(sizeError);
      return;
    }

    setIsCbsUploading(true);
    setCbsUploadError("");

    try {
      const text = await pdfToText(file);
      const parsedPayload = parseCbsData(text);
      const enquiryDate = extractCbsEnquiryDate(text);

      if (!parsedPayload) {
        throw new Error(
          "CBS report not detected. Please upload the original CBS PDF.",
        );
      }

      await uploadSupplementaryDocument({
        file,
        documentType: "cbs",
        parsedPayload: {
          ...parsedPayload,
          enquiry_date: enquiryDate || null,
        },
      });
    } catch (error) {
      setCbsUploadError(error.message || "Unable to upload CBS report.");
    } finally {
      setIsCbsUploading(false);
    }
  };

  const handleMlcbUpload = async (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!selectedGroupId) {
      setMlcbUploadError(
        "Create or select a group before uploading an MLCB report.",
      );
      return;
    }

    const sizeError = validatePortalUploadSize([file], "MLCB report");
    if (sizeError) {
      setMlcbUploadError(sizeError);
      return;
    }

    setIsMlcbUploading(true);
    setMlcbUploadError("");

    try {
      await uploadSupplementaryDocument({
        file,
        documentType: "mlcb",
      });
    } catch (error) {
      setMlcbUploadError(error.message || "Unable to upload MLCB report.");
    } finally {
      setIsMlcbUploading(false);
    }
  };

  const handleResetGroupJobs = async () => {
    if (!selectedGroupId || isResettingGroupJobs) {
      return;
    }

    const statementCount = Number(selectedGroupProgress.total || 0);
    const confirmed = window.confirm(
      `Wipe parsed data and put ${statementCount} bank statement ${
        statementCount === 1 ? "job" : "jobs"
      } back in queue?`,
    );

    if (!confirmed) {
      return;
    }

    setIsResettingGroupJobs(true);
    setGroupError("");

    try {
      const response = await fetch(
        `${API_BASE}/bank-statement-portal/groups/${selectedGroupId}/reset-bank-document-jobs`,
        {
          method: "POST",
          headers: authHeaders,
        },
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(
          responseErrorMessage(response, json, "Unable to wipe group data."),
        );
      }

      if (json.group) {
        setGroups((current) =>
          current.map((group) =>
            String(group.id) === String(json.group.id) ? json.group : group,
          ),
        );
        setGroupDetailState((current) => ({
          ...current,
          loading: false,
          error: null,
          statementsLoading: false,
          statementsError: null,
          reportLoading: false,
          reportError: null,
          group: json.group,
          submissions: json.submissions || [],
          statements: [],
          report: null,
          questionAssistant: null,
        }));
      }

      await Promise.all([
        loadDashboard({ silent: true }),
        loadGroupDetail(selectedGroupId, { silent: true }),
      ]);
    } catch (error) {
      setGroupDetailState((current) => ({
        ...current,
        error: error.message || "Unable to wipe group data.",
      }));
    } finally {
      setIsResettingGroupJobs(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/bank-statement-portal/logout`, {
        method: "POST",
        headers: authHeaders,
      });
    } finally {
      handleUnauthorized();
    }
  };

  const handleDownloadReport = async () => {
    const reportNode = reportRef.current;

    if (!groupDetailState.report || !reportNode || !selectedGroup) {
      return;
    }

    setIsDownloadingReport(true);

    let exportWrapper = null;

    try {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
        putOnlyUsedFonts: true,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const exportPageWidthPx = 1480;
      const exportPageHeightPx = Math.floor(
        (exportPageWidthPx * pageHeight) / pageWidth,
      );
      const exportCaptureScale = 1.2;
      const exportImageQuality = 0.68;
      exportWrapper = document.createElement("div");
      const exportClone = reportNode.cloneNode(true);

      await new Promise((resolve) => window.setTimeout(resolve, 80));

      exportWrapper.setAttribute("aria-hidden", "true");
      exportWrapper.setAttribute("data-report-export", "true");
      exportWrapper.style.position = "fixed";
      exportWrapper.style.left = "-10000px";
      exportWrapper.style.top = "0";
      exportWrapper.style.width = `${exportPageWidthPx}px`;
      exportWrapper.style.background = "#ffffff";
      exportWrapper.style.zIndex = "-1";
      exportWrapper.style.pointerEvents = "none";

      exportWrapper.appendChild(exportClone);
      document.body.appendChild(exportWrapper);

      const relaxScrollableOverflow = (node) => {
        Array.from(node.querySelectorAll("*")).forEach((element) => {
          const computedStyle = window.getComputedStyle(element);
          if (computedStyle.overflowX === "auto" || computedStyle.overflowX === "scroll") {
            element.style.overflowX = "visible";
          }
          if (computedStyle.overflowY === "auto" || computedStyle.overflowY === "scroll") {
            element.style.overflowY = "visible";
          }
        });
      };

      relaxScrollableOverflow(exportClone);

      const originalHeader = exportClone.children[0];
      const originalContent = exportClone.children[1];

      if (!originalHeader || !originalContent) {
        throw new Error("Unable to prepare the report export layout.");
      }

      const buildExportPage = () => {
        const pageNode = exportClone.cloneNode(false);
        pageNode.style.width = `${exportPageWidthPx}px`;
        pageNode.style.maxWidth = `${exportPageWidthPx}px`;
        pageNode.style.minWidth = `${exportPageWidthPx}px`;
        pageNode.style.margin = "0";
        pageNode.style.overflow = "visible";
        pageNode.style.boxSizing = "border-box";

        const headerNode = originalHeader.cloneNode(true);
        headerNode.style.position = "relative";
        headerNode.style.top = "auto";

        const contentNode = originalContent.cloneNode(false);
        contentNode.style.minHeight = "0";
        contentNode.style.overflow = "visible";

        pageNode.appendChild(headerNode);
        pageNode.appendChild(contentNode);
        exportWrapper.appendChild(pageNode);

        return { pageNode, contentNode };
      };

      exportWrapper.innerHTML = "";

      let currentPage = buildExportPage();
      const sectionNodes = Array.from(originalContent.children).map((node) =>
        node.cloneNode(true),
      );

      if (combinedAnalysisRef.current) {
        const combinedAnalysisNode = combinedAnalysisRef.current.cloneNode(true);
        combinedAnalysisNode.style.marginTop = "24px";
        combinedAnalysisNode.style.minWidth = "0";
        combinedAnalysisNode.style.maxWidth = "100%";
        combinedAnalysisNode.style.boxSizing = "border-box";
        relaxScrollableOverflow(combinedAnalysisNode);
        sectionNodes.push(combinedAnalysisNode);
      }

      sectionNodes.forEach((sectionNode) => {
        currentPage.contentNode.appendChild(sectionNode);

        if (
          currentPage.pageNode.scrollHeight > exportPageHeightPx &&
          currentPage.contentNode.children.length > 1
        ) {
          currentPage.contentNode.removeChild(sectionNode);
          currentPage = buildExportPage();
          currentPage.contentNode.appendChild(sectionNode);
        }
      });

      const exportPages = Array.from(exportWrapper.children);

      for (let index = 0; index < exportPages.length; index += 1) {
        const canvas = await html2canvas(exportPages[index], {
          scale: exportCaptureScale,
          useCORS: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
          windowWidth: exportPages[index].scrollWidth,
          windowHeight: exportPages[index].scrollHeight,
          logging: false,
        });

        if (index > 0) {
          pdf.addPage();
        }

        const renderedHeight = (canvas.height * pageWidth) / canvas.width;
        pdf.addImage(
          canvas.toDataURL("image/jpeg", exportImageQuality),
          "JPEG",
          0,
          0,
          pageWidth,
          Math.min(pageHeight, renderedHeight),
          undefined,
          "FAST",
        );
      }

      document.body.removeChild(exportWrapper);
      exportWrapper = null;

      const safeGroupName = (selectedGroup.name || "bank-statement-group-report")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      pdf.save(`${safeGroupName || "bank-statement-group-report"}.pdf`);
    } catch (error) {
      if (exportWrapper?.parentNode) {
        exportWrapper.parentNode.removeChild(exportWrapper);
      }
      setGroupDetailState((current) => ({
        ...current,
        error: error?.message || "Unable to download the lending report.",
      }));
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const showMobileGroupDetail = isMobile && mobileView === "group";
  const cbsObligationRows = getCbsObligationRows(groupDetailState.cbsReports);
  const cbsMonthlyInstalmentHistories = getCbsMonthlyInstalmentHistories(
    groupDetailState.cbsReports,
  );

  const portalIdentitySection = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <div>
        <div style={smallLabelStyle}>Portal user</div>
        <div style={{ fontSize: "20px", fontWeight: 800 }}>
          {me?.name || Cookies.get("bank_statement_portal_name") || "-"}
        </div>
      </div>
      <div
        style={{
          padding: "10px 14px",
          borderRadius: "16px",
          background: "#12304a",
          color: "#fff",
          minWidth: "96px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "12px", opacity: 0.8 }}>Tokens</div>
        <div style={{ fontSize: "28px", fontWeight: 900 }}>
          {me?.tokens_remaining ?? "-"}
        </div>
      </div>
    </div>
  );

  const createGroupSection = (
    <form
      onSubmit={handleCreateGroup}
      style={{
        marginTop: "20px",
        padding: "18px",
        borderRadius: "18px",
        background: "#f3f8fc",
        border: "1px dashed rgba(14, 90, 165, 0.25)",
      }}
    >
      <div style={smallLabelStyle}>Create a group</div>
      <div style={{ color: "#617487", fontSize: "14px", marginTop: "6px" }}>
        Give this statement set a name first, then upload every file that belongs
        in the same report.
      </div>
      {groupError ? (
        <div style={{ ...errorBannerStyle, marginTop: "14px" }}>{groupError}</div>
      ) : null}
      <input
        value={groupName}
        onChange={(event) => setGroupName(event.target.value)}
        placeholder="Example: Feb 2026 operating accounts"
        maxLength={255}
        style={textInputStyle}
      />
      <button
        type="submit"
        disabled={isCreatingGroup}
        style={{
          ...primaryButtonStyle,
          width: "100%",
          marginTop: "14px",
          opacity: isCreatingGroup ? 0.7 : 1,
          cursor: isCreatingGroup ? "default" : "pointer",
        }}
      >
        {isCreatingGroup ? "Creating group..." : "Create Group"}
      </button>
    </form>
  );

  const uploadSection = (
    <div
      style={{
        marginTop: "18px",
        padding: "18px",
        borderRadius: "18px",
        background: "#fff8ef",
        border: "1px dashed rgba(196, 123, 13, 0.22)",
      }}
    >
      <div style={smallLabelStyle}>Upload into selected group</div>
      <div style={{ color: "#617487", fontSize: "14px", marginTop: "6px" }}>
        {selectedGroup
          ? `Selected group: ${selectedGroup.name}`
          : "Create or pick a group first."}
      </div>
      {uploadError ? (
        <div style={{ ...errorBannerStyle, marginTop: "14px" }}>{uploadError}</div>
      ) : null}
      <label
        htmlFor="portal-upload"
        style={{
          ...primaryButtonStyle,
          display: "block",
          textAlign: "center",
          marginTop: "16px",
          opacity:
            isUploading || !selectedGroupId || (me?.tokens_remaining ?? 0) < 1
              ? 0.6
              : 1,
          cursor:
            isUploading || !selectedGroupId || (me?.tokens_remaining ?? 0) < 1
              ? "default"
              : "pointer",
        }}
      >
        {isUploading ? "Uploading..." : "Upload Bank Statements"}
      </label>
      <input
        id="portal-upload"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        multiple
        onChange={handleUpload}
        disabled={
          isUploading || !selectedGroupId || (me?.tokens_remaining ?? 0) < 1
        }
        style={{ display: "none" }}
      />
      <div style={{ marginTop: "12px", fontSize: "12px", color: "#617487" }}>
        PDF pages use 1 token per page. JPG, JPEG, and PNG use 1 token each.
      </div>
    </div>
  );

  const supplementaryUploadSection = (
    <div
      style={{
        marginTop: "18px",
        display: "grid",
        gap: "12px",
      }}
    >
      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          background: "#f4fbf5",
          border: "1px dashed rgba(17, 121, 73, 0.22)",
        }}
      >
        <div style={smallLabelStyle}>Upload CBS Report</div>
        <div style={{ color: "#617487", fontSize: "14px", marginTop: "6px" }}>
          Upload the CBS PDF separately from the bank statements for this group.
        </div>
        {cbsUploadError ? (
          <div style={{ ...errorBannerStyle, marginTop: "14px" }}>
            {cbsUploadError}
          </div>
        ) : null}
        <label
          htmlFor="portal-cbs-upload"
          style={{
            ...secondaryButtonStyle,
            display: "block",
            textAlign: "center",
            marginTop: "16px",
            opacity: isCbsUploading || !selectedGroupId ? 0.6 : 1,
            cursor: isCbsUploading || !selectedGroupId ? "default" : "pointer",
          }}
        >
          {isCbsUploading ? "Uploading CBS..." : "Upload CBS Report"}
        </label>
        <input
          id="portal-cbs-upload"
          type="file"
          accept=".pdf"
          onChange={handleCbsUpload}
          disabled={isCbsUploading || !selectedGroupId}
          style={{ display: "none" }}
        />
      </div>

      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          background: "#f8f4ff",
          border: "1px dashed rgba(87, 66, 179, 0.22)",
        }}
      >
        <div style={smallLabelStyle}>Upload MLCB Report</div>
        <div style={{ color: "#617487", fontSize: "14px", marginTop: "6px" }}>
          Upload the MLCB PDF separately. The existing OCR worker will process it
          after upload.
        </div>
        {mlcbUploadError ? (
          <div style={{ ...errorBannerStyle, marginTop: "14px" }}>
            {mlcbUploadError}
          </div>
        ) : null}
        <label
          htmlFor="portal-mlcb-upload"
          style={{
            ...secondaryButtonStyle,
            display: "block",
            textAlign: "center",
            marginTop: "16px",
            opacity: isMlcbUploading || !selectedGroupId ? 0.6 : 1,
            cursor:
              isMlcbUploading || !selectedGroupId ? "default" : "pointer",
          }}
        >
          {isMlcbUploading ? "Uploading MLCB..." : "Upload MLCB Report"}
        </label>
        <input
          id="portal-mlcb-upload"
          type="file"
          accept=".pdf"
          onChange={handleMlcbUpload}
          disabled={isMlcbUploading || !selectedGroupId}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );

  const groupsSection = (
    <div style={{ marginTop: "22px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={smallLabelStyle}>Statement groups</div>
        {isPolling ? (
          <div style={{ fontSize: "12px", color: "#617487" }}>
            Refreshing progress...
          </div>
        ) : null}
      </div>
      <div style={{ marginTop: "12px", display: "grid", gap: "10px" }}>
        <input
          type="search"
          value={groupSearchQuery}
          onChange={(event) => setGroupSearchQuery(event.target.value)}
          placeholder="Search groups by name"
          aria-label="Search groups by name"
          style={{
            ...textInputStyle,
            marginTop: 0,
          }}
        />
        {isLoading ? (
          <div style={mutedCardStyle}>Loading groups...</div>
        ) : groups.length === 0 ? (
          <div style={mutedCardStyle}>
            No groups yet. Create one to start uploading statements.
          </div>
        ) : filteredGroups.length === 0 ? (
          <div style={mutedCardStyle}>
            No groups match "{groupSearchQuery.trim()}".
          </div>
        ) : (
          filteredGroups.map((group) => {
            const isActive = String(group.id) === String(selectedGroupId);
            const progress = isActive
              ? selectedGroupProgress
              : group.progress || defaultProgress;
            const displayStatus = isActive ? selectedGroup?.status : group.status;
            const statusStyle = statusPillStyle(displayStatus);

            return (
              <button
                key={group.id}
                onClick={() => handleSelectGroup(group.id)}
                style={{
                  border: isActive
                    ? "1px solid #0e5aa5"
                    : "1px solid rgba(18,48,74,0.08)",
                  borderRadius: "16px",
                  background: isActive ? "#eef6ff" : "#fff",
                  padding: "14px",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#12304a",
                      wordBreak: "break-word",
                      flex: 1,
                    }}
                  >
                    {group.name}
                  </div>
                  <span
                    style={{
                      ...groupStatusPillStyle,
                      ...statusStyle,
                    }}
                  >
                    {displayStatus}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: "10px",
                    height: "8px",
                    borderRadius: "999px",
                    background: "rgba(18,48,74,0.08)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${progress.percent || 0}%`,
                      minWidth: progress.percent ? "18px" : 0,
                      borderRadius: "999px",
                      background:
                        displayStatus === "completed"
                          ? "linear-gradient(90deg, #117949 0%, #24a16a 100%)"
                          : "linear-gradient(90deg, #0e5aa5 0%, #69a6f0 50%, #0e5aa5 100%)",
                      backgroundSize:
                        displayStatus === "completed" ? "100% 100%" : "120px 100%",
                      animation:
                        displayStatus === "processing"
                          ? "portalProgressShift 1.8s linear infinite"
                          : "none",
                      transition: "width 450ms ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginTop: "10px",
                    fontSize: "13px",
                    color: "#617487",
                  }}
                >
                  <span>
                    {progress.completed}/{progress.total} completed
                  </span>
                  <span>{progress.percent}%</span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  const groupDetailCard = (
    <div
      style={{
        ...cardStyle,
        padding: isMobile ? "12px" : "22px",
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "flex-start",
          flexWrap: "wrap",
          marginBottom: "18px",
        }}
      >
        <div>
          {isMobile ? (
            <button
              type="button"
              onClick={() => setMobileView("groups")}
              style={{
                ...secondaryButtonStyle,
                padding: "10px 14px",
                marginBottom: "12px",
              }}
            >
              Back to Groups
            </button>
          ) : null}
          <div style={smallLabelStyle}>Group overview</div>
          <div style={{ fontSize: isMobile ? "20px" : "22px", fontWeight: 800 }}>
            {selectedGroup?.name || "Select a group"}
          </div>
          {selectedGroup?.created_at ? (
            <div style={{ color: "#617487", marginTop: "6px", fontSize: "14px" }}>
              Created {formatDate(selectedGroup.created_at)}
            </div>
          ) : null}
        </div>
        {selectedGroup ? (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: isMobile ? "flex-start" : "flex-end",
            }}
          >
            {canResetGroupJobs ? (
              <button
                type="button"
                onClick={handleResetGroupJobs}
                disabled={
                  isResettingGroupJobs ||
                  !selectedGroupProgress.total ||
                  groupDetailState.loading
                }
                style={{
                  ...secondaryButtonStyle,
                  padding: "10px 14px",
                  borderColor: "rgba(181, 61, 46, 0.28)",
                  color: "#b53d2e",
                  background: "rgba(181, 61, 46, 0.06)",
                  opacity:
                    isResettingGroupJobs ||
                    !selectedGroupProgress.total ||
                    groupDetailState.loading
                      ? 0.6
                      : 1,
                  cursor:
                    isResettingGroupJobs ||
                    !selectedGroupProgress.total ||
                    groupDetailState.loading
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {isResettingGroupJobs ? "Wiping..." : "Wipe data"}
              </button>
            ) : null}
            <div
              style={{
                ...groupStatusPillStyle,
                ...statusPillStyle(selectedGroup.status),
                textTransform: "capitalize",
              }}
            >
              {selectedGroup.status}
            </div>
          </div>
        ) : null}
      </div>

      {!selectedGroupId ? (
        <div style={mutedCardStyle}>
          Pick a group to review its uploads and combined bank statement analysis.
        </div>
      ) : (
        <>
          {isMobile ? uploadSection : null}
          {isMobile ? supplementaryUploadSection : null}

          <div style={progressCardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={smallLabelStyle}>Parsing progress</div>
                <div style={{ fontSize: "28px", fontWeight: 900, marginTop: "4px" }}>
                  {selectedGroupProgress.percent}%
                </div>
              </div>
              <div style={{ color: "#617487", fontSize: "14px" }}>
                {selectedGroupProgress.completed} of {selectedGroupProgress.total}{" "}
                files completed
              </div>
            </div>
            <div
              style={{
                marginTop: "16px",
                height: "16px",
                borderRadius: "999px",
                background: "rgba(18,48,74,0.08)",
                overflow: "hidden",
                boxShadow: "inset 0 1px 2px rgba(18,48,74,0.06)",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${selectedGroupProgress.percent || 0}%`,
                  minWidth: selectedGroupProgress.percent ? "24px" : 0,
                  borderRadius: "999px",
                  background:
                    selectedGroup?.status === "completed"
                      ? "linear-gradient(90deg, #117949 0%, #39c482 100%)"
                      : "linear-gradient(90deg, #0e5aa5 0%, #8cc2ff 45%, #0e5aa5 100%)",
                  backgroundSize:
                    selectedGroup?.status === "completed" ? "100% 100%" : "140px 100%",
                  animation:
                    selectedGroup?.status === "processing"
                      ? "portalProgressShift 1.5s linear infinite"
                      : "none",
                  transition: "width 500ms ease",
                }}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "12px",
                marginTop: "16px",
              }}
            >
              <div style={progressMetricStyle}>
                <div style={progressMetricLabelStyle}>Completed</div>
                <div style={progressMetricValueStyle}>
                  {selectedGroupProgress.completed}
                </div>
              </div>
              <div style={progressMetricStyle}>
                <div style={progressMetricLabelStyle}>Processing</div>
                <div style={progressMetricValueStyle}>
                  {selectedGroupProgress.processing}
                </div>
              </div>
              <div style={progressMetricStyle}>
                <div style={progressMetricLabelStyle}>Queued</div>
                <div style={progressMetricValueStyle}>
                  {selectedGroupProgress.queued}
                </div>
              </div>
              <div style={progressMetricStyle}>
                <div style={progressMetricLabelStyle}>Files</div>
                <div style={progressMetricValueStyle}>
                  {selectedGroupProgress.total}
                </div>
              </div>
            </div>
          </div>

          {selectedGroup?.status === "processing" ? (
            <div
              style={{
                marginTop: "14px",
                padding: "12px 14px",
                borderRadius: "12px",
                background: "#f7fbff",
                color: "#0e5aa5",
                border: "1px solid rgba(14, 90, 165, 0.14)",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              This group is still processing. The page checks the backend every
              10 seconds and the progress bar will move as more statements finish
              parsing.
            </div>
          ) : null}

          <div style={{ marginTop: "22px" }}>
            <div style={smallLabelStyle}>Files in this group</div>
            <div style={{ marginTop: "12px", display: "grid", gap: "10px" }}>
              {selectedGroupSubmissions.length === 0 ? (
                <div style={mutedCardStyle}>No files uploaded to this group yet.</div>
              ) : (
                selectedGroupSubmissions.map((submission) => {
                  const fraudCheck = fraudChecksByJobId.get(
                    String(submission.bank_document_job_id),
                  );
                  const fraudScore = Number(fraudCheck?.risk_score);
                  const hasFraudScore = Number.isFinite(fraudScore);

                  return (
                    <div
                      key={submission.id}
                      style={{
                        ...submissionRowStyle,
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {submission.document_url ? (
                          <a
                            href={submission.document_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: 800,
                              wordBreak: "break-word",
                              color: "#0e5aa5",
                              textDecoration: "none",
                            }}
                          >
                            <span>{submission.original_file_name}</span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#617487",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Open file
                            </span>
                          </a>
                        ) : (
                          <div
                            style={{
                              fontWeight: 800,
                              wordBreak: "break-word",
                            }}
                          >
                            {submission.original_file_name}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            flexWrap: "wrap",
                            marginTop: "8px",
                            fontSize: "13px",
                            color: "#617487",
                          }}
                        >
                          <span>
                            Uploaded{" "}
                            {submission.created_at
                              ? formatDateTime(submission.created_at)
                              : "-"}
                          </span>
                          <span>
                            Parsed{" "}
                            {submission.parsed_completed_at
                              ? formatDateTime(submission.parsed_completed_at)
                              : "-"}
                          </span>
                          <span>
                            Time taken{" "}
                            {formatDurationSeconds(
                              submission.job_claimed_at,
                              submission.job_updated_at,
                            )}
                          </span>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: isMobile ? "flex-start" : "flex-end",
                        }}
                      >
                        {fraudCheck ? (
                          <span
                            title={
                              hasFraudScore
                                ? `Fraud risk score ${fraudScore}/100`
                                : "Fraud checks detected"
                            }
                            style={{
                              ...groupStatusPillStyle,
                              ...fraudRiskPillStyle(
                                fraudCheck.risk_score,
                                fraudCheck.risk_level,
                              ),
                              whiteSpace: "nowrap",
                            }}
                          >
                            Risk {hasFraudScore ? `${fraudScore}/100` : "-"}
                          </span>
                        ) : null}
                        <span
                          style={{
                            ...groupStatusPillStyle,
                            ...statusPillStyle(submission.status),
                            textTransform: "capitalize",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {submission.status}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div style={{ marginTop: "24px" }}>
            <div style={smallLabelStyle}>CBS Reports</div>
            <div style={{ marginTop: "12px", display: "grid", gap: "10px" }}>
              {(groupDetailState.cbsReports || []).length === 0 ? (
                <div style={mutedCardStyle}>
                  No CBS reports uploaded for this group yet.
                </div>
              ) : (
                groupDetailState.cbsReports.map((report) => (
                  <div key={report.id} style={submissionRowStyle}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <a
                        href={report.document_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 800,
                          wordBreak: "break-word",
                          color: "#117949",
                          textDecoration: "none",
                        }}
                      >
                        <span>{report.original_file_name}</span>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#617487",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Open file
                        </span>
                      </a>
                      <div
                        style={{
                          marginTop: "12px",
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>Credit Score</div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {report.parsed_payload?.CreditScore ??
                              report.parsed_payload?.credit_score ??
                              "-"}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>Risk Grade</div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {report.parsed_payload?.RiskGrade ||
                              report.parsed_payload?.risk_grade ||
                              "-"}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>
                            Probability Default
                          </div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {report.parsed_payload?.ProbabilityofDefault ??
                              report.parsed_payload?.probability_default ??
                              "-"}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>
                            Secured Limit
                          </div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {formatAmount(
                              report.parsed_payload?.SecuredCreditLimit ??
                                report.parsed_payload?.secured_credit_limit,
                            )}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>
                            Unsecured Limit
                          </div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {formatAmount(
                              report.parsed_payload?.UnsecuredCreditLimit ??
                                report.parsed_payload?.unsecured_credit_limit,
                            )}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>
                            Secured Utilized
                          </div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {formatAmount(report.parsed_payload?.SecuredCreditUtilized)}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>
                            Unsecured Utilized
                          </div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {formatAmount(report.parsed_payload?.UnsecuredCreditUtilized)}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>
                            Monthly Commitment
                          </div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {formatAmount(report.parsed_payload?.MonthlyCommitment)}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>Defaults</div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {report.parsed_payload?.defaults ? "True" : "N/A"}
                          </div>
                        </div>
                        <div style={progressMetricStyle}>
                          <div style={progressMetricLabelStyle}>
                            Bankruptcy Proceedings
                          </div>
                          <div style={{ marginTop: "6px", fontWeight: 800 }}>
                            {report.parsed_payload?.bankcruptcyProceedings
                              ? "True"
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                      {(report.parsed_payload?.cbs_obligations || []).length > 0 ? (
                        <div
                          style={{
                            marginTop: "14px",
                            overflowX: "auto",
                            border: "1px solid rgba(18,48,74,0.08)",
                            borderRadius: "14px",
                          }}
                        >
                          <div
                            style={{
                              padding: "12px 14px",
                              background: "#f7fbf8",
                              borderBottom: "1px solid rgba(18,48,74,0.08)",
                            }}
                          >
                            <div style={smallLabelStyle}>CBS detected obligations</div>
                            <div
                              style={{
                                marginTop: "4px",
                                color: "#617487",
                                fontSize: "12px",
                                lineHeight: 1.5,
                              }}
                            >
                              These balances and instalments were detected from
                              the CBS report, not from the bank statements.
                            </div>
                          </div>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                <th style={tableHeaderCellStyle}>Grantor bank</th>
                                <th style={tableHeaderCellStyle}>Facility</th>
                                <th
                                  style={{
                                    ...tableHeaderCellStyle,
                                    textAlign: "right",
                                  }}
                                >
                                  Outstanding balance
                                </th>
                                <th
                                  style={{
                                    ...tableHeaderCellStyle,
                                    textAlign: "right",
                                  }}
                                >
                                  Monthly instalment
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {report.parsed_payload.cbs_obligations.map(
                                (obligation, index) => (
                                    <tr key={`${report.id}-cbs-obligation-${index}`}>
                                      <td style={tableCellStyle}>
                                        {obligation.grantor_bank || "-"}
                                      </td>
                                      <td style={tableCellStyle}>
                                        {obligation.facility || "CBS facility"}
                                      </td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          textAlign: "right",
                                        }}
                                      >
                                        {formatAmount(
                                          obligation.outstanding_balance,
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          textAlign: "right",
                                        }}
                                      >
                                        {formatAmount(
                                          obligation.monthly_instalment,
                                        )}
                                      </td>
                                    </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                    <span
                      style={{
                        ...groupStatusPillStyle,
                        ...statusPillStyle(report.status),
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {report.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ marginTop: "24px" }}>
            <div style={smallLabelStyle}>MLCB Reports</div>
            <div style={{ marginTop: "12px", display: "grid", gap: "10px" }}>
              {(groupDetailState.mlcbReports || []).length === 0 ? (
                <div style={mutedCardStyle}>
                  No MLCB reports uploaded for this group yet.
                </div>
              ) : (
                groupDetailState.mlcbReports.map((report) => (
                  <div key={report.id} style={submissionRowStyle}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <a
                        href={report.document_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 800,
                          wordBreak: "break-word",
                          color: "#5d3fd3",
                          textDecoration: "none",
                        }}
                      >
                        <span>{report.original_file_name}</span>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#617487",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Open file
                        </span>
                      </a>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          flexWrap: "wrap",
                          marginTop: "8px",
                          fontSize: "13px",
                          color: "#617487",
                        }}
                      >
                        <span>
                          Uploaded{" "}
                          {report.created_at ? formatDateTime(report.created_at) : "-"}
                        </span>
                        <span>
                          Parsed{" "}
                          {report.parsed_completed_at
                            ? formatDateTime(report.parsed_completed_at)
                            : "-"}
                        </span>
                      </div>
                      {report.status === "processing" ? (
                        <div
                          style={{
                            marginTop: "12px",
                            padding: "12px 14px",
                            borderRadius: "12px",
                            background: "#f7f2ff",
                            color: "#5d3fd3",
                            border: "1px solid rgba(93, 63, 211, 0.16)",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          OCR is still running for this MLCB report. The portal
                          will keep polling and this section will refresh when the
                          analysis is ready.
                        </div>
                      ) : null}
                      {(report.entries || []).length > 0 ? (
                        <div style={{ marginTop: "14px", display: "grid", gap: "10px" }}>
                          {report.entries.map((entry, index) => (
                            <div
                              key={`${report.id}-${index}`}
                              style={{
                                padding: "14px",
                                borderRadius: "14px",
                                border: "1px solid rgba(18,48,74,0.08)",
                                background: "#fcfbff",
                              }}
                            >
                              <div style={{ fontWeight: 800 }}>
                                {entry.issued_by || "Issuer unavailable"}
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit, minmax(180px, 1fr))",
                                  gap: "10px",
                                  marginTop: "10px",
                                  color: "#617487",
                                  fontSize: "13px",
                                }}
                              >
                                <div>
                                  Date of Loan: {entry.date_of_loan || "-"}
                                </div>
                                <div>
                                  Total Payable Amount:{" "}
                                  {formatAmount(entry.total_payable_amount)}
                                </div>
                              </div>
                              {entry.months ? (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    display: "flex",
                                    gap: "8px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {Object.entries(entry.months)
                                    .sort(([left], [right]) =>
                                      left.localeCompare(right),
                                    )
                                    .map(([month, status]) => (
                                      <span
                                        key={`${report.id}-${index}-${month}`}
                                        style={{
                                          padding: "6px 10px",
                                          borderRadius: "999px",
                                          background: "rgba(93, 63, 211, 0.08)",
                                          color: "#5d3fd3",
                                          fontSize: "12px",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {month}: {status}
                                      </span>
                                    ))}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <span
                      style={{
                        ...groupStatusPillStyle,
                        ...statusPillStyle(report.status),
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {report.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ marginTop: "24px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={smallLabelStyle}>Lending report</div>
                <div style={{ color: "#617487", marginTop: "6px", fontSize: "14px" }}>
                  A lender-style credit memo built from every completed statement in
                  this group, ready to review or export.
                </div>
              </div>
              <button
                onClick={handleDownloadReport}
                disabled={!groupDetailState.report || isDownloadingReport}
                style={{
                  ...secondaryButtonStyle,
                  width: isMobile ? "100%" : "auto",
                  opacity: !groupDetailState.report || isDownloadingReport ? 0.6 : 1,
                  cursor:
                    !groupDetailState.report || isDownloadingReport
                      ? "default"
                      : "pointer",
                }}
              >
                {isDownloadingReport ? "Preparing PDF..." : "Download Report"}
              </button>
            </div>
            <div style={{ marginTop: "14px", minWidth: 0, maxWidth: "100%" }}>
              {groupDetailState.reportLoading ? (
                <div style={reportGeneratingCardStyle}>
                  <div style={reportGeneratingSpinnerStyle} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: 800,
                        color: "#12304a",
                      }}
                    >
                      {reportLoadingMessage}
                    </div>
                    <div
                      style={{
                        marginTop: "5px",
                        fontSize: "13px",
                        color: "#617487",
                        lineHeight: 1.5,
                      }}
                    >
                      Keep this page open while the report is prepared.
                    </div>
                    <div style={reportGeneratingBarTrackStyle}>
                      <div style={reportGeneratingBarStyle} />
                    </div>
                  </div>
                </div>
              ) : groupDetailState.report ? (
                <>
                  <BankStatementGroupReport
                    report={groupDetailState.report}
                    groupName={selectedGroup?.name}
                    isMobile={isMobile}
                    reportRef={reportRef}
                    cbsObligations={cbsObligationRows}
                    cbsMonthlyInstalmentHistories={cbsMonthlyInstalmentHistories}
                    savedScenario={selectedGroup?.report_scenario}
                    onScenarioChange={saveReportScenario}
                    savingScenario={isSavingReportScenario}
                  />
                  <div ref={combinedAnalysisRef} style={{ marginTop: "24px" }}>
                    <div style={smallLabelStyle}>Combined analysis</div>
                    <div
                      style={{
                        color: "#617487",
                        marginTop: "6px",
                        fontSize: "14px",
                      }}
                    >
                      Parsed statements from every completed file in this group are
                      merged here for detailed review alongside the credit memo.
                    </div>
                    <div style={{ marginTop: "14px" }}>
                      <BankStatementAnalysisPanel
                        enabled={true}
                        loading={
                          groupDetailState.loading ||
                          groupDetailState.statementsLoading
                        }
                        error={
                          groupDetailState.statementsError ||
                          groupDetailState.error
                        }
                        statementGroups={groupedAnalysisStatements}
                        isMobile={isMobile}
                        formatAmount={formatAmount}
                        formatDate={formatDate}
                        emptyMessage="Analysis is not ready yet for this group."
                      />
                    </div>
                  </div>
                </>
              ) : groupDetailState.reportError ? (
                <div style={errorBannerStyle}>{groupDetailState.reportError}</div>
              ) : (
                <div style={mutedCardStyle}>
                  The lending report will appear once at least one file in this
                  group has completed parsing.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(14,90,165,0.12), transparent 35%), #fffdf8",
        padding: isMobile ? "8px" : isTablet ? "18px" : "24px",
        fontFamily: "Manrope, sans-serif",
        color: "#12304a",
      }}
    >
      <style>{`
        @keyframes portalProgressShift {
          0% { background-position: 0 0; }
          100% { background-position: 120px 0; }
        }
        @keyframes reportSpinnerRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes reportLoadingSweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(280%); }
        }
      `}</style>

      <div
        style={{
          width: "100%",
          maxWidth: isMobile ? "100%" : "85vw",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
            alignItems: isMobile ? "stretch" : "center",
            flexWrap: "wrap",
            marginBottom: isMobile ? "12px" : "24px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#0e5aa5",
                marginBottom: isMobile ? "6px" : "8px",
              }}
            >
              SmartLend
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: isMobile ? "24px" : "34px",
                lineHeight: 1.05,
              }}
            >
              Bank statement AI portal
            </h1>
            <p
              style={{
                color: "#617487",
                margin: isMobile ? "8px 0 0" : "10px 0 0",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              Create a named group, upload as many statements as you need, and
              watch the parsing progress update as each file completes.
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              ...secondaryButtonStyle,
              width: isMobile ? "100%" : "auto",
            }}
          >
            Sign Out
          </button>
        </div>

        {pageError ? <div style={errorBannerStyle}>{pageError}</div> : null}

        {isMobile ? (
          showMobileGroupDetail ? (
            groupDetailCard
          ) : (
            <div style={{ ...cardStyle, padding: "12px" }}>
              {portalIdentitySection}
              <div
                style={{
                  marginTop: "14px",
                  padding: "14px",
                  borderRadius: "18px",
                  background:
                    "linear-gradient(135deg, rgba(14,90,165,0.08) 0%, rgba(255,248,239,0.8) 100%)",
                  border: "1px solid rgba(18,48,74,0.08)",
                }}
              >
                <div style={smallLabelStyle}>Mobile flow</div>
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "18px",
                    fontWeight: 800,
                    color: "#12304a",
                  }}
                >
                  Pick or create a group first
                </div>
                <div style={{ marginTop: "8px", color: "#617487", fontSize: "14px" }}>
                  Start here on mobile, then open a group to upload bank
                  statements, CBS reports, and MLCB reports separately while you
                  review the analysis.
                </div>
              </div>
              {createGroupSection}
              {groupsSection}
            </div>
          )
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isTablet
                ? "1fr"
                : "minmax(300px, 360px) minmax(0, 1fr)",
              gap: isMobile ? "14px" : "20px",
            }}
          >
            <div style={{ ...cardStyle, padding: isMobile ? "12px" : "22px" }}>
              {portalIdentitySection}
              {createGroupSection}
              {uploadSection}
              {supplementaryUploadSection}
              {groupsSection}
            </div>

            {groupDetailCard}
          </div>
        )}
      </div>

      <BankStatementQuestionAssistant
        questionAssistant={groupDetailState.questionAssistant}
        groupId={selectedGroup?.id}
        groupName={selectedGroup?.name}
        authHeaders={authHeaders}
        onUnauthorized={handleUnauthorized}
        isMobile={isMobile}
        enabled={
          (!isMobile || showMobileGroupDetail) &&
          Boolean(groupDetailState.questionAssistant) &&
          selectedGroup?.status === "completed"
        }
      />
    </div>
  );
};

const cardStyle = {
  background: "rgba(255,255,255,0.9)",
  border: "1px solid rgba(18,48,74,0.08)",
  borderRadius: "20px",
  padding: "22px",
  boxShadow: "0 20px 45px rgba(18,48,74,0.08)",
};

const primaryButtonStyle = {
  border: "none",
  borderRadius: "14px",
  padding: "14px 16px",
  background: "#0e5aa5",
  color: "#fff",
  fontWeight: 800,
};

const secondaryButtonStyle = {
  border: "1px solid rgba(18,48,74,0.14)",
  borderRadius: "14px",
  padding: "12px 16px",
  background: "#fff",
  color: "#12304a",
  fontWeight: 800,
  cursor: "pointer",
};

const textInputStyle = {
  width: "100%",
  marginTop: "14px",
  borderRadius: "14px",
  border: "1px solid rgba(18,48,74,0.12)",
  padding: "14px 16px",
  outline: "none",
  fontSize: "14px",
  color: "#12304a",
  background: "#fff",
};

const smallLabelStyle = {
  fontSize: "12px",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontWeight: 800,
  color: "#0e5aa5",
};

const tableHeaderCellStyle = {
  ...smallLabelStyle,
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid rgba(18,48,74,0.08)",
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  padding: "12px",
  borderBottom: "1px solid rgba(18,48,74,0.08)",
  color: "#12304a",
  fontSize: "13px",
  verticalAlign: "top",
};

const mutedCardStyle = {
  borderRadius: "16px",
  background: "#f8fafc",
  border: "1px dashed rgba(18,48,74,0.12)",
  padding: "18px",
  color: "#617487",
};

const reportGeneratingCardStyle = {
  borderRadius: "16px",
  background: "linear-gradient(180deg, #ffffff 0%, #f5f9fd 100%)",
  border: "1px solid rgba(14, 90, 165, 0.18)",
  padding: "16px",
  marginBottom: "14px",
  color: "#12304a",
  display: "flex",
  alignItems: "center",
  gap: "14px",
  boxShadow: "0 12px 28px rgba(18,48,74,0.08)",
};

const reportGeneratingSpinnerStyle = {
  width: "34px",
  height: "34px",
  borderRadius: "50%",
  border: "3px solid rgba(14,90,165,0.16)",
  borderTopColor: "#0e5aa5",
  flex: "0 0 auto",
  animation: "reportSpinnerRotate 0.9s linear infinite",
};

const reportGeneratingBarTrackStyle = {
  marginTop: "12px",
  height: "7px",
  borderRadius: "999px",
  background: "rgba(14,90,165,0.12)",
  overflow: "hidden",
};

const reportGeneratingBarStyle = {
  width: "38%",
  height: "100%",
  borderRadius: "999px",
  background: "linear-gradient(90deg, rgba(14,90,165,0), #0e5aa5, rgba(14,90,165,0))",
  animation: "reportLoadingSweep 1.6s ease-in-out infinite",
};

const errorBannerStyle = {
  background: "#fff1f0",
  color: "#cf1322",
  border: "1px solid #ffa39e",
  borderRadius: "12px",
  padding: "12px 14px",
};

const groupStatusPillStyle = {
  padding: "8px 12px",
  borderRadius: "999px",
  fontWeight: 700,
  fontSize: "12px",
};

const progressCardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background:
    "linear-gradient(180deg, rgba(243,248,252,0.92) 0%, rgba(255,255,255,0.95) 100%)",
  border: "1px solid rgba(18,48,74,0.08)",
};

const progressMetricStyle = {
  padding: "14px",
  borderRadius: "16px",
  background: "#fff",
  border: "1px solid rgba(18,48,74,0.08)",
};

const progressMetricLabelStyle = {
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#617487",
  fontWeight: 800,
};

const progressMetricValueStyle = {
  marginTop: "8px",
  fontSize: "24px",
  fontWeight: 900,
  color: "#12304a",
};

const submissionRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  alignItems: "flex-start",
  padding: "16px",
  borderRadius: "16px",
  border: "1px solid rgba(18,48,74,0.08)",
  background: "#fff",
};

export default PortalDashboard;
